import React, { useEffect, useState, useCallback } from "react";
import styled from "@emotion/styled";
import LocationItem from "./LocationItem";
import GuestItem from "./GuestItem";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../store";
import BackButton from "./BackButton";
import { useNavigate } from "react-router-dom";
import {
  useGetTicketDataQuery,
  useGetLocationEmployeesQuery,
} from "../services/managersApi";
import { setTicketData, setNotes } from "../store/checkoutSlice";
import { skipToken } from "@reduxjs/toolkit/query";
import { createBlankTicket } from "../utils/createBlankTicket";
import { formatCurrency } from "../../web-redemption/TipOptions";
import { setError } from "../store/uiSlice";
import { useEmployeeSelection } from "../hooks/useEmployeeSelection";
import EmployeeDropdown from "./EmployeeDropdown";
import * as S from "./Refund.styles";

const Container = styled.div`
  position: relative;
  height: 100%;
`;

const Content = styled.div`
  @media (min-width: 768px) {
    display: flex;
    flex-direction: column;
  }
`;

const InfoSection = styled.div`
  width: 100%;
  margin-top: 24px;
  margin-bottom: 24px;
`;

const Title = styled.h1`
  color: #fff;
  font-family: "Rethink-Sans-Bold", "sans-serif";
  font-size: 26px;
  text-align: center;
  margin-bottom: 8px;
  margin-top: 40px;

  @media (min-width: 768px) {
    font-size: 32px;
  }
`;

const Subtitle = styled.p`
  color: #fff;
  font-family: "Rethink-Sans-Regular", "sans-serif";
  font-size: 17px;
  text-align: center;
  margin-bottom: 32px;

  @media (min-width: 768px) {
    font-size: 20px;
  }
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background: #393939;
  margin: 16px 0;
`;

const Callout = styled.div`
  color: #fff;
  opacity: 0.5;
  font-family: "Rethink-Sans-Bold", "sans-serif";
  font-size: 17px;
  text-align: center;
  margin-bottom: 8px;

  @media (min-width: 768px) {
    font-size: 20px;
  }
`;

const Link = styled.button`
  color: #007aff;
  font-family: "Rethink-Sans-Medium", "sans-serif";
  font-size: 17px;
  background: none;
  border: none;
  cursor: pointer;
  text-align: center;
  margin-bottom: 32px;

  @media (min-width: 768px) {
    font-size: 20px;
  }
`;

const StyledLinkContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Input = styled.input`
  width: 100%;
  padding: 16px;
  font-size: 20px;
  border-radius: 12px;
  border: none;
  background: #232323;
  color: #fff;
  margin-bottom: 32px;
  outline: none;
  text-align: center;

  @media (min-width: 768px) {
    font-size: 22px;
  }
`;

const NextButton = styled.button`
  width: 100%;
  background: #fbc94b;
  color: #000;
  font-family: "Rethink-Sans-SemiBold", "sans-serif";
  font-size: 16px;
  border: none;
  border-radius: 9999px;
  padding: 16px 0;
  margin-top: 16px;
  cursor: pointer;
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (min-width: 768px) {
    font-size: 20px;
    max-width: 400px;
    align-self: center;
  }
`;

export default function EnterCheckNumber() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useSelector(
    (state: RootState) => state.checkout.selectedLocation,
  );
  const isLegacyLocation = location?.pos_type === "legacy";
  const [checkNumber, setCheckNumber] = useState("");
  const [showAmount, setShowAmount] = useState(isLegacyLocation);
  const [checkNotes, setCheckNotes] = useState<string>("");
  const [amount, setAmount] = useState("$0.00");
  const [submitCheckNumber, setSubmitCheckNumber] = useState("");
  const selectedGuest = useSelector(
    (state: RootState) => state.checkout.selectedGuest,
  );
  const {
    data: ticketData,
    error,
    isFetching,
  } = useGetTicketDataQuery(
    submitCheckNumber && location
      ? { locationId: location.id ?? 0, ticketNumber: submitCheckNumber }
      : skipToken,
  );

  const {
    employees,
    selectedEmployee,
    dropdownOpen,
    isLoadingEmployees,
    employeesError,
    selectEmployee,
    toggleDropdown,
  } = useEmployeeSelection(location?.id);
  useEffect(() => {
    if (ticketData) {
      dispatch(setTicketData(ticketData.ticket));
      navigate("/checkout");
    }
  }, [ticketData, dispatch]);

  useEffect(() => {
    if (!isFetching) {
      setSubmitCheckNumber("");
    }
  }, [isFetching]);

  useEffect(() => {
    if (error) {
      const errorMessage =
        (error as any)?.data?.errors?.base?.[0] ??
        "Failed to fetch ticket data. Please try again.";
      dispatch(setError(errorMessage));
      setCheckNumber("");
      setTimeout(() => {
        dispatch(setError(""));
      }, 5000);
    }
  }, [error, dispatch]);

  const handleAmountChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;
      const numericValue = value.replace(/\D/g, "");

      if (numericValue === "") {
        setAmount("$0.00");
        return;
      }

      const formattedValue = formatCurrency(numericValue);
      setAmount(formattedValue);
    },
    [],
  );
  const isAmountValid = () => {
    if (!amount) return false;
    const numeric = parseFloat(amount.replace(/[$,]/g, ""));
    return numeric > 0;
  };
  return (
    <Container>
      <BackButton onClick={() => window.history.back()} />
      <Content>
        <InfoSection>
          {location && (
            <LocationItem
              location={location}
              isDiner={true}
              hideAddress={true}
            />
          )}
          {selectedGuest && <GuestItem guest={selectedGuest} mode="dark" />}
          <Divider />
        </InfoSection>
        <Title>{showAmount ? "Enter Bill Amount" : "Enter Check Number"}</Title>
        <Subtitle>
          {showAmount
            ? "Please enter the bill amount from the top of the bill"
            : "Please enter the check number from the top of the bill"}
        </Subtitle>
        <Input
          type="text"
          placeholder={showAmount ? "Bill Amount" : "Check Number"}
          value={showAmount ? amount : checkNumber}
          onChange={(e) =>
            showAmount ? handleAmountChange(e) : setCheckNumber(e.target.value)
          }
        />
        {isLegacyLocation && employees.length > 1 ? (
          <EmployeeDropdown
            employees={employees}
            selectedEmployee={selectedEmployee}
            isOpen={dropdownOpen}
            isLoading={isLoadingEmployees}
            error={employeesError}
            onToggle={toggleDropdown}
            onSelect={selectEmployee}
          />
        ) : null}
        {!showAmount && (
          <StyledLinkContainer>
            <Callout>Don’t have a check number?</Callout>
            <Link onClick={() => setShowAmount(true)}>Enter bill amount →</Link>
          </StyledLinkContainer>
        )}
        <S.FormField>
          <S.Label>
            Notes <S.OptionalLabel>(Optional)</S.OptionalLabel>
          </S.Label>
          <S.TextArea
            value={checkNotes}
            onChange={(e) => setCheckNotes(e.target.value)}
            placeholder="Optional notes about the bill..."
          />
        </S.FormField>
        <br />
        <NextButton
          disabled={
            (!showAmount && !checkNumber) || (showAmount && !isAmountValid())
          }
          onClick={() => {
            if (!showAmount && checkNumber) {
              //check number entry
              setSubmitCheckNumber(checkNumber);
            }
            if (showAmount && amount) {
              //manual amount entry
              dispatch(
                setTicketData(
                  createBlankTicket(amount, selectedEmployee?.pin).ticket,
                ),
              );
              dispatch(setNotes(checkNotes));
              navigate("/checkout");
            }
          }}
        >
          {isFetching ? "Loading..." : "Next"}
        </NextButton>
      </Content>
    </Container>
  );
}
